import {getCollection} from '@/lib/filters/filters';
import {hydrateSlimComps, hydrateSlimMarkers} from '@/lib/filters/hydrateSlimMarkers';
import {firestoreCollectionByCollectionName} from '@/internal/store/collections';
import type {SlimMarker} from '@/services/FilterService';

const ENTRATA_COLLECTION = 'entrata-multifamily-properties';

/** Multi-family projects come from two sources; hydrate each id from the doc store it lives in. */
async function hydrateMultiFamilyProjects(items: SlimMarker[]): Promise<unknown[]> {
  const entrata = items.filter((item) => item.source === 'entrata');
  const salesforce = items.filter((item) => item.source !== 'entrata');
  const [hydratedEntrata, hydratedSalesforce] = await Promise.all([
    hydrateSlimMarkers(ENTRATA_COLLECTION, entrata),
    hydrateSlimMarkers(firestoreCollectionByCollectionName.multiFamilyProjects, salesforce),
  ]);
  const byId = new Map([...hydratedEntrata, ...hydratedSalesforce].map((item) => [item.id, item]));
  // Re-assemble in the original order.
  return items.map((item) => byId.get(item.id)).filter((item) => item !== undefined);
}

async function hydrateItems(collectionName: string, items: unknown[]): Promise<unknown[]> {
  if (!items.length) return items;
  switch (collectionName) {
    case 'multiFamilyProjects': {
      return hydrateMultiFamilyProjects(items as SlimMarker[]);
    }
    case 'competitiveBuildingComps': {
      return hydrateSlimComps(items as {id: string; parent?: SlimMarker}[]);
    }
    default: {
      const firestoreCollection = firestoreCollectionByCollectionName[collectionName];
      // Collections without a Firestore mapping (overlays, features) hold full client-side objects.
      if (!firestoreCollection) return items;
      return hydrateSlimMarkers(firestoreCollection, items as SlimMarker[]);
    }
  }
}

/**
 * Gathers the filtered-in items of each collection for a shared-map export.
 *
 * The filter registry holds slim markers, which lack the fields the export pipeline formats;
 * this hydrates them into full docs first. A collection that fails
 * to hydrate contributes an empty list instead of failing the whole gather, and its name is
 * reported in `failures` so the caller can surface the problem to the user.
 */
export async function gatherExportItems(
  collectionNames: string[],
): Promise<{items: Map<string, unknown[]>; failures: string[]}> {
  // Each collection hydrates independently, so gather them concurrently rather than one at a time.
  const gathered = await Promise.all(
    collectionNames.map(async (collectionName): Promise<{name: string; items: unknown[]; failed?: boolean} | null> => {
      const collection = getCollection(collectionName);
      if (!collection) {
        console.warn(`Collection "${collectionName}" not found.`);
        return null;
      }

      const items = collection.getFiltered(false) as unknown[];
      try {
        return {name: collectionName, items: await hydrateItems(collectionName, items)};
      } catch (error) {
        console.error(`Failed to hydrate '${collectionName}' for export:`, error);
        return {name: collectionName, items: [], failed: true};
      }
    }),
  );

  // Rebuild in the original collection order (Promise.all preserves it); report any that failed.
  const items = new Map<string, unknown[]>();
  const failures: string[] = [];
  for (const entry of gathered) {
    if (!entry) continue;
    items.set(entry.name, entry.items);
    if (entry.failed) {
      failures.push(entry.name);
    }
  }
  return {items, failures};
}
